import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Printer, RefreshCw, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageHeader, StatCard } from "@/components/common";
import { api, errMsg, fmtTime } from "@/lib/api";
import { cn } from "@/lib/utils";

const KIND = { order: "Pedido do garçom", delivery: "Delivery", bill: "Conta", sale: "Venda", partial: "Pagamento parcial" };
const STATUS = {
  pending: ["Na fila", "bg-amber-500/15 text-amber-600 dark:text-amber-400", "fila"],
  printing: ["Imprimindo", "bg-sky-500/15 text-sky-600 dark:text-sky-400", "fila"],
  done: ["Impresso", "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", "impressas"],
  failed: ["Falhou", "bg-red-500/15 text-red-600 dark:text-red-400", "falhas"],
  expired: ["Expirado", "bg-red-500/10 text-red-500", "falhas"],
};
const FILTERS = [["todas", "Todas"], ["impressas", "Impressas"], ["fila", "Na fila"], ["falhas", "Com falha"]];
const groupOf = (j) => STATUS[j.status]?.[2] || "fila";

function CentralInfo({ central, now }) {
  const on = central?.seen_at && new Date(now) - new Date(central.seen_at) < 120000;
  return (
    <div className={cn("flex items-start gap-2 rounded-lg border px-4 py-3 text-sm", on ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/10")} data-testid="print-central-info">
      <Printer className={cn("mt-0.5 h-4 w-4 shrink-0", on ? "text-emerald-500" : "text-amber-500")} />
      {on ? <span>Computador de impressão ativo: <b>{central.device}</b></span>
        : <span>Nenhum computador de impressão ativo agora{central?.device ? ` (último: ${central.device} às ${fmtTime(central.seen_at)})` : ""}. Abra o sistema no computador que tem o QZ Tray e a impressora.</span>}
    </div>
  );
}

function JobRow({ j, busy, onRetry, onReprint }) {
  const [label, tone] = STATUS[j.status] || [j.status, ""];
  const failed = j.status === "failed" || j.status === "expired";
  const info = [KIND[j.kind] || j.kind, `por ${j.created_by}`, j.device, j.attempts > 1 && `${j.attempts} tentativas`, j.printed_at && `impresso às ${fmtTime(j.printed_at)}`].filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-3 transition-colors last:border-0 hover:bg-accent/40" data-testid={`print-job-${j.id}`}>
      <span className="w-12 font-mono text-sm tabular-nums text-muted-foreground">{fmtTime(j.created_at)}</span>
      <div className="min-w-[12rem] flex-1">
        <p className="font-medium leading-tight">{j.title || KIND[j.kind]}</p>
        <p className="text-xs text-muted-foreground">{info.join(" · ")}</p>
        {failed && j.error && <p className="mt-1 text-xs text-red-500" data-testid={`print-job-error-${j.id}`}>{j.error}</p>}
      </div>
      <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", tone)} data-testid={`print-job-status-${j.id}`}>{label}</span>
      <div className="flex gap-2">
        {failed && <Button size="sm" disabled={busy} onClick={() => onRetry(j)} data-testid={`print-job-retry-${j.id}`}><RotateCcw className="mr-1 h-4 w-4" />Tentar de novo</Button>}
        {!["pending", "printing"].includes(j.status) && <Button size="sm" variant="outline" disabled={busy} onClick={() => onReprint(j)} data-testid={`print-job-reprint-${j.id}`}><Printer className="mr-1 h-4 w-4" />Reimprimir</Button>}
      </div>
    </div>
  );
}

export default function PrintJobsPage() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("todas");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const load = useCallback((loud) => api.get("/print-jobs/today").then((r) => setData(r.data)).catch((e) => { if (loud) toast.error(errMsg(e)); }), []);
  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 10000);
    return () => clearInterval(t);
  }, [load]);
  const act = async (j, path, ok) => {
    setBusy(j.id);
    try { await api.post(`/print-jobs/${j.id}/${path}`); toast.success(ok); await load(true); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const jobs = useMemo(() => data?.jobs || [], [data]);
  const counts = useMemo(() => jobs.reduce((c, j) => ({ ...c, [groupOf(j)]: (c[groupOf(j)] || 0) + 1 }), {}), [jobs]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return jobs.filter((j) => (filter === "todas" || groupOf(j) === filter) && (!s || `${j.title} ${j.created_by} ${KIND[j.kind]}`.toLowerCase().includes(s)));
  }, [jobs, filter, q]);
  return (
    <div className="space-y-6" data-testid="print-jobs-page">
      <PageHeader eyebrow="Operação" title="Impressões" subtitle="Tudo o que foi para a impressora hoje. Reimprima uma 2ª via ou tente de novo em um clique."
        actions={<Button variant="outline" onClick={() => load(true)} data-testid="print-jobs-refresh-btn"><RefreshCw className="mr-2 h-4 w-4" />Atualizar</Button>} />
      {data && <CentralInfo central={data.central} now={data.now} />}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Hoje" value={jobs.length} icon={Printer} testId="print-stat-total" />
        <StatCard label="Impressas" value={counts.impressas || 0} icon={CheckCircle2} tone="success" testId="print-stat-done" />
        <StatCard label="Na fila" value={counts.fila || 0} icon={Clock} tone="warning" testId="print-stat-queue" />
        <StatCard label="Com falha" value={counts.falhas || 0} icon={AlertTriangle} tone="danger" testId="print-stat-failed" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, l]) => <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} className="rounded-full" onClick={() => setFilter(k)} data-testid={`print-filter-${k}`}>{l}</Button>)}
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número, nome ou tipo" className="pl-9" data-testid="print-jobs-search" />
        </div>
      </div>
      <div className="card-surface overflow-hidden" data-testid="print-jobs-list">
        {list.map((j) => <JobRow key={j.id} j={j} busy={busy === j.id} onRetry={(x) => act(x, "retry", "Enviada de novo para a impressora")} onReprint={(x) => act(x, "reprint", "2ª via enviada para a impressora")} />)}
        {data && !list.length && <EmptyState icon={Printer} title={jobs.length ? "Nada neste filtro" : "Nenhuma impressão hoje"} text={jobs.length ? "Troque o filtro ou a busca para ver as outras impressões." : "Quando um pedido, conta ou venda for para a impressora, ele aparece aqui."} />}
      </div>
    </div>
  );
}
